/**
 * The widget's height, for the store's other floating controls to clear on a phone (boxed's
 * `useMobileSiblingLift`): there the widget's rows span the screen's width above the tab bar, where
 * the support chat button also sits. While a row shows, the widget's rendered height is published
 * as a CSS variable on `<html>`, and the button adds it to its own offset; on a wide screen the
 * widget sits at the other corner and nothing reads it.
 */
import { type RefObject, useEffect } from "react";

/** The variable: the widget's height in pixels, unset while no row shows. */
export const WIDGET_LIFT_VAR = "--live-breaks-widget-lift";

/** Publishes the height of `ref`'s first child (the widget) while `active`. */
export function useWidgetLift(ref: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const widget = ref.current?.firstElementChild;
    if (!(widget instanceof HTMLElement)) return;
    const root = document.documentElement;
    const update = () =>
      root.style.setProperty(WIDGET_LIFT_VAR, `${widget.getBoundingClientRect().height}px`);
    update();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(widget);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(WIDGET_LIFT_VAR);
    };
  }, [ref, active]);
}
